import { useState, useRef, useEffect } from 'react';
import { useBetSlip } from '../../../components/feature/BetSlipContext';
import PageHero from '../../../components/feature/PageHero';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PropOption {
  label: string;   // e.g. "OVER 230.5"
  prob: string;
  price: string;
  payout: string;
  profit: string;
}

interface PlayerPropCard {
  sport: 'NFL' | 'NBA';
  category: 'PASSING' | 'RUSHING' | 'RECEIVING' | 'SCORING' | 'ASSISTS' | 'REBOUNDS';
  categoryColor: string;
  playerName: string;
  playerMeta: string; // e.g. "SEA • QB #14"
  playerAvatar: string;
  statLabel: string;  // e.g. "Passing Yards"
  line: string;       // e.g. "230.5 • Super Bowl LX"
  volume: string;     // e.g. "45.2k"
  options: [PropOption, PropOption];
  game: 'Super Bowl LX' | 'AFC Championship' | 'NFC Championship';
}

// ─── Category Badge Colors ────────────────────────────────────────────────────

const CAT_COLORS: Record<string, { bg: string; text: string }> = {
  PASSING:   { bg: 'bg-sky-500/20',    text: 'text-sky-400' },
  RUSHING:   { bg: 'bg-amber-500/20',  text: 'text-amber-400' },
  RECEIVING: { bg: 'bg-teal-500/20',   text: 'text-teal-400' },
  SCORING:   { bg: 'bg-orange-500/20', text: 'text-orange-400' },
  ASSISTS:   { bg: 'bg-violet-500/20', text: 'text-violet-400' },
  REBOUNDS:  { bg: 'bg-emerald-500/20',text: 'text-emerald-400' },
};

// ─── Mock Data ────────────────────────────────────────────────────────────────

const ALL_PROPS: PlayerPropCard[] = [
  // Super Bowl LX
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Sam Darnold', playerMeta: 'SEA • QB #14',
    playerAvatar: 'https://public.readdy.ai/ai/img_res/dfc19eaa3640f873c82623bced7a0398.jpg',
    statLabel: 'Passing Yards', line: '230.5 • Super Bowl LX', volume: '45.2k',
    options: [
      { label: 'OVER 230.5',  prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
      { label: 'UNDER 230.5', prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Sam Darnold', playerMeta: 'SEA • QB #14',
    playerAvatar: 'https://readdy.ai/api/search-image?query=professional%20american%20football%20quarterback%20player%20portrait%20headshot%20wearing%20helmet%2C%20dark%20studio%20background%2C%20dramatic%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-sam-darnold-2&orientation=squarish',
    statLabel: 'Passing TDs', line: '1.5 • Super Bowl LX', volume: '36.1k',
    options: [
      { label: 'OVER 1.5',  prob: '55%', price: '$1.00', payout: '$1.81', profit: '+$0.81' },
      { label: 'UNDER 1.5', prob: '45%', price: '$1.00', payout: '$2.21', profit: '+$1.21' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Drake Maye', playerMeta: 'NE • QB #10',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20quarterback%20young%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-drake-maye-1&orientation=squarish',
    statLabel: 'Passing Yards', line: '220.5 • Super Bowl LX', volume: '42.8k',
    options: [
      { label: 'OVER 220.5',  prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
      { label: 'UNDER 220.5', prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'RUSHING', categoryColor: 'amber',
    playerName: 'Kenneth Walker III', playerMeta: 'SEA • RB #9',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20running%20back%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-kenneth-walker-1&orientation=squarish',
    statLabel: 'Rushing Yards', line: '71.5 • Super Bowl LX', volume: '22.3k',
    options: [
      { label: 'OVER 71.5',  prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
      { label: 'UNDER 71.5', prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'RUSHING', categoryColor: 'amber',
    playerName: 'Rhamondre Stevenson', playerMeta: 'NE • RB #38',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20running%20back%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-rhamondre-1&orientation=squarish',
    statLabel: 'Rushing Yards', line: '50.5 • Super Bowl LX', volume: '35.6k',
    options: [
      { label: 'OVER 50.5',  prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
      { label: 'UNDER 50.5', prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'RECEIVING', categoryColor: 'teal',
    playerName: 'Jaxon Smith-Njigba', playerMeta: 'SEA • WR #11',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20wide%20receiver%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-jsn-1&orientation=squarish',
    statLabel: 'Receiving Yards', line: '96.5 • Super Bowl LX', volume: '48.9k',
    options: [
      { label: 'OVER 96.5',  prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
      { label: 'UNDER 96.5', prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'RECEIVING', categoryColor: 'teal',
    playerName: 'Jaxon Smith-Njigba', playerMeta: 'SEA • WR #11',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20wide%20receiver%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-jsn-2&orientation=squarish',
    statLabel: 'Receptions', line: '7.5 • Super Bowl LX', volume: '31.2k',
    options: [
      { label: 'OVER 7.5',  prob: '46%', price: '$1.00', payout: '$2.16', profit: '+$1.16' },
      { label: 'UNDER 7.5', prob: '54%', price: '$1.00', payout: '$1.84', profit: '+$0.84' },
    ],
    game: 'Super Bowl LX',
  },
  {
    sport: 'NFL', category: 'RECEIVING', categoryColor: 'teal',
    playerName: 'Kayshon Boutte', playerMeta: 'NE • WR #1',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20wide%20receiver%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-kayshon-1&orientation=squarish',
    statLabel: 'Receiving Yards', line: '45.5 • Super Bowl LX', volume: '28.4k',
    options: [
      { label: 'OVER 45.5',  prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
      { label: 'UNDER 45.5', prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
    ],
    game: 'Super Bowl LX',
  },
  // AFC Championship
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Patrick Mahomes', playerMeta: 'KC • QB #15',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20quarterback%20star%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-mahomes-1&orientation=squarish',
    statLabel: 'Passing Yards', line: '275.5 • AFC Championship', volume: '62.1k',
    options: [
      { label: 'OVER 275.5',  prob: '50%', price: '$1.00', payout: '$1.99', profit: '+$0.99' },
      { label: 'UNDER 275.5', prob: '50%', price: '$1.00', payout: '$1.99', profit: '+$0.99' },
    ],
    game: 'AFC Championship',
  },
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Josh Allen', playerMeta: 'BUF • QB #17',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20quarterback%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-josh-allen-1&orientation=squarish',
    statLabel: 'Passing Yards', line: '265.5 • AFC Championship', volume: '58.7k',
    options: [
      { label: 'OVER 265.5',  prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
      { label: 'UNDER 265.5', prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
    ],
    game: 'AFC Championship',
  },
  {
    sport: 'NFL', category: 'RUSHING', categoryColor: 'amber',
    playerName: 'Josh Allen', playerMeta: 'BUF • QB #17',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20quarterback%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-josh-allen-2&orientation=squarish',
    statLabel: 'Rushing Yards', line: '35.5 • AFC Championship', volume: '41.3k',
    options: [
      { label: 'OVER 35.5',  prob: '52%', price: '$1.00', payout: '$1.91', profit: '+$0.91' },
      { label: 'UNDER 35.5', prob: '48%', price: '$1.00', payout: '$2.07', profit: '+$1.07' },
    ],
    game: 'AFC Championship',
  },
  // NFC Championship
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Jalen Hurts', playerMeta: 'PHI • QB #1',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20quarterback%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-jalen-hurts-1&orientation=squarish',
    statLabel: 'Passing Yards', line: '225.5 • NFC Championship', volume: '53.4k',
    options: [
      { label: 'OVER 225.5',  prob: '49%', price: '$1.00', payout: '$2.03', profit: '+$1.03' },
      { label: 'UNDER 225.5', prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
    ],
    game: 'NFC Championship',
  },
  {
    sport: 'NFL', category: 'PASSING', categoryColor: 'sky',
    playerName: 'Brock Purdy', playerMeta: 'SF • QB #13',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20quarterback%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-brock-purdy-1&orientation=squarish',
    statLabel: 'Passing Yards', line: '255.5 • NFC Championship', volume: '51.8k',
    options: [
      { label: 'OVER 255.5',  prob: '50%', price: '$1.00', payout: '$1.99', profit: '+$0.99' },
      { label: 'UNDER 255.5', prob: '50%', price: '$1.00', payout: '$1.99', profit: '+$0.99' },
    ],
    game: 'NFC Championship',
  },
  {
    sport: 'NFL', category: 'RUSHING', categoryColor: 'amber',
    playerName: 'Christian McCaffrey', playerMeta: 'SF • RB #23',
    playerAvatar: 'https://readdy.ai/api/search-image?query=american%20football%20running%20back%20player%20portrait%20headshot%20in%20uniform%2C%20dark%20studio%20background%2C%20dramatic%20sports%20lighting%2C%20photorealistic&width=48&height=48&seq=pp-cmc-1&orientation=squarish',
    statLabel: 'Rushing Yards', line: '85.5 • NFC Championship', volume: '67.2k',
    options: [
      { label: 'OVER 85.5',  prob: '52%', price: '$1.00', payout: '$1.91', profit: '+$0.91' },
      { label: 'UNDER 85.5', prob: '48%', price: '$1.00', payout: '$2.07', profit: '+$1.07' },
    ],
    game: 'NFC Championship',
  },
];

// ─── Game Filter Tabs ─────────────────────────────────────────────────────────

const GAME_TABS = [
  { key: 'all',              label: 'All Games' },
  { key: 'Super Bowl LX',    label: 'Super Bowl LX' },
  { key: 'AFC Championship', label: 'AFC Championship' },
  { key: 'NFC Championship', label: 'NFC Championship' },
];

const STAT_TABS = [
  { key: 'all',      label: 'All',       icon: 'ri-apps-2-line' },
  { key: 'PASSING',  label: 'Passing',   icon: 'ri-football-line' },
  { key: 'RUSHING',  label: 'Rushing',   icon: 'ri-run-line' },
  { key: 'RECEIVING',label: 'Receiving', icon: 'ri-hand-coin-line' },
  { key: 'DEFENSE',  label: 'Defense',   icon: 'ri-shield-line' },
];

const SUBNAV = [
  { key: 'all',          label: 'All',          icon: 'ri-apps-2-line',           href: '/markets' },
  { key: 'spreads',      label: 'Spreads',       icon: 'ri-bar-chart-grouped-line', href: '/markets/spreads' },
  { key: 'money-lines',  label: 'Money Lines',   icon: 'ri-copper-coin-line',       href: '/markets/money-lines' },
  { key: 'futures',      label: 'Futures',       icon: 'ri-calendar-event-line',    href: '/markets/futures' },
  { key: 'parlays',      label: 'Parlays',       icon: 'ri-stack-line',             href: '/markets/parlays' },
  { key: 'player-props', label: 'Player Props',  icon: 'ri-user-star-line',         href: '/markets/player-props' },
];

const marketSubLinks = [
  { href: '/markets/spreads',      icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
  { href: '/markets/money-lines',  icon: 'ri-copper-coin-line',       label: 'Money Lines' },
  { href: '/markets/futures',      icon: 'ri-calendar-event-line',    label: 'Futures' },
  { href: '/markets/parlays',      icon: 'ri-stack-line',             label: 'Parlays' },
  { href: '/markets/player-props', icon: 'ri-user-star-line',         label: 'Player Props' },
];

// ─── Player Prop Card ─────────────────────────────────────────────────────────

function PlayerPropCardItem({ card }: { card: PlayerPropCard }) {
  const [selected, setSelected] = useState<0 | 1>(0);
  const [added, setAdded] = useState(false);
  const { addBet } = useBetSlip();
  const cat = CAT_COLORS[card.category] ?? { bg: 'bg-slate-500/20', text: 'text-slate-400' };
  const sel = card.options[selected];

  const handleAddToSlip = () => {
    addBet({
      id: `prop-${card.playerName}-${card.statLabel}-${sel.label}`.replace(/\s+/g, '-').toLowerCase(),
      market: `${card.sport} Player Props — ${card.category}`,
      matchup: `${card.playerName} ${card.statLabel}`,
      pick: sel.label,
      price: sel.price,
      payout: sel.payout,
      profit: sel.profit,
      badge: card.sport,
      badgeColor: card.sport === 'NFL' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="rounded-xl border border-slate-700/80 overflow-hidden flex flex-col" style={{ background: '#1e2330' }}>
      {/* Category Badge */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <span className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${cat.bg} ${cat.text}`}>
          <i className="ri-user-star-line text-xs"></i>
          {card.category}
        </span>
      </div>

      {/* Player Info */}
      <div className="px-4 pb-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-slate-600/60">
          <img
            src={card.playerAvatar}
            alt={card.playerName}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-white leading-tight">{card.playerName}</div>
          <div className="text-xs text-slate-400">{card.playerMeta}</div>
        </div>
      </div>

      {/* Stat Line */}
      <div className="px-4 pb-2">
        <div className="text-xs font-bold text-white mb-0.5">{card.statLabel}</div>
        <div className="text-xs text-slate-400">Line: {card.line}</div>
        <div className="flex items-center gap-1 mt-1">
          <i className="ri-line-chart-line text-teal-400 text-xs"></i>
          <span className="text-xs text-slate-400">Vol: <span className="text-teal-400 font-semibold">{card.volume}</span></span>
        </div>
      </div>

      {/* Over / Under Rows */}
      <div className="px-3 pb-3 space-y-1.5 flex-1">
        {card.options.map((opt, idx) => (
          <div
            key={idx}
            onClick={() => setSelected(idx as 0 | 1)}
            className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all border ${
              selected === idx
                ? 'border-teal-500/60 bg-teal-500/10'
                : 'border-slate-700/60 bg-slate-800/40 hover:border-slate-600'
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-bold ${selected === idx ? 'text-teal-300' : 'text-slate-200'}`}>{opt.label}</div>
              <div className="text-xs text-slate-500">Prob: {opt.prob}</div>
            </div>
            <div className="text-right pl-3 flex-shrink-0">
              <div className="text-xs text-slate-400">Price: {opt.price}</div>
              <div className="text-xs text-slate-300 font-medium">Payout: {opt.payout}</div>
              <div className="text-xs text-teal-400 font-semibold">{opt.profit}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Add to Slip */}
      <div className="px-3 pb-4 pt-1">
        <button
          onClick={handleAddToSlip}
          className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
            added
              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
              : 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/20'
          }`}
        >
          <i className={`text-sm ${added ? 'ri-check-line' : 'ri-add-circle-line'}`}></i>
          {added ? 'Added!' : 'Add to Slip'}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MarketsPlayerPropsPage() {
  const [activeGame, setActiveGame] = useState('all');
  const [activeStat, setActiveStat] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);
  const { items: slipItems, toggleSlip } = useBetSlip();

  const filtered = ALL_PROPS.filter(p => {
    const gameMatch = activeGame === 'all' || p.game === activeGame;
    const statMatch = activeStat === 'all' || p.category === activeStat;
    return gameMatch && statMatch;
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-player-props';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Player Props — L2L Sports Prediction Market',
      description: 'Trade player prop markets on passing yards, rushing yards, receiving yards, touchdowns, and more. Zero juice player props.',
      url: `${siteUrl}/markets/player-props`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Markets', item: `${siteUrl}/markets` },
          { '@type': 'ListItem', position: 3, name: 'Player Props', item: `${siteUrl}/markets/player-props` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50 transition-colors duration-300" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 group flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen(o => !o)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    {marketSubLinks.map(link => (
                      <a key={link.href} href={link.href} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${link.href === '/markets/player-props' ? 'bg-teal-500/10 text-teal-400' : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'}`}>
                          <i className={`${link.icon} w-4 h-4 flex items-center justify-center ${link.href === '/markets/player-props' ? 'text-teal-400' : 'text-slate-500'}`}></i>
                          {link.label}
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-apps-2-line w-4 h-4 flex items-center justify-center text-slate-500"></i>
                          All Markets
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <a href="/why-rtj"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>Why L2L</div></a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button onClick={toggleSlip} className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer" title="Bet Slip">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                {slipItems.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{slipItems.length}</span>}
              </button>
              <a href="/my-account"><div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"><i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i></div></a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer" aria-label="Toggle menu">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'All Markets' },
                { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
                { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
                { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
                { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
                { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
                { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        title="Player Props"
        subtitle="Bet on individual player performance — points, yards, strikeouts, and more"
        badgeLabel="Live Player Props Markets"
        badgeColor="bg-sky-500/20 border-sky-500/40 text-sky-400"
        backgroundImage="https://readdy.ai/api/search-image?query=professional%20athlete%20close%20up%20action%20shots%20basketball%20player%20dunking%20football%20quarterback%20throwing%20baseball%20pitcher%20hockey%20player%20shooting%20intense%20focus%20determination%20sports%20performance%20individual%20excellence%20dramatic%20lighting&width=1920&height=600&seq=playerprops-hero-bg&orientation=landscape"
      />

      {/* Sub Navigation */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-2">
            {SUBNAV.map(sub => (
              <a key={sub.key} href={sub.href}>
                <div className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  sub.key === 'player-props'
                    ? 'bg-slate-700 text-white border border-slate-500/60'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                }`}>
                  <i className={`${sub.icon} text-sm ${sub.key === 'player-props' ? 'text-teal-400' : 'text-slate-500'}`}></i>
                  {sub.label}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Game Filter Tabs */}
      <div className="border-b border-slate-700/50" style={{ background: 'rgba(30,30,30,0.9)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            {GAME_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveGame(tab.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap border ${
                  activeGame === tab.key
                    ? 'bg-teal-500 border-teal-500 text-white shadow-lg shadow-teal-500/20'
                    : 'bg-transparent border-slate-600 text-slate-400 hover:border-slate-400 hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stat Category Filter */}
      <div className="border-b border-slate-700/40" style={{ background: 'rgba(25,25,25,0.9)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            {STAT_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveStat(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                  activeStat === tab.key
                    ? 'bg-slate-700 border-slate-500 text-white'
                    : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                }`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <section className="py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((card, idx) => (
                <PlayerPropCardItem key={idx} card={card} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-slate-500">
              <i className="ri-user-star-line text-4xl mb-3 block"></i>
              <p>No player prop markets match the selected filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4 mt-8" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
